import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Button,
  Chip,
  Icon,
  Textarea,
  Toast,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { formatDateTime } from "@/utils";
import { RESEARCH_ID_FIELDS } from "@/utils/researchIds";
import {
  pipelineService,
  type Testimonial,
  type UnlinkTestimonialType,
} from "@/api/pipeline.service";
import { usePermissions } from "@/hooks/usePermissions";
import { TESTIMONIALS_UNLINK_LAWYER_PERMISSION } from "@/constants/permissions";
import LoadingState from "@/components/LoadingState";
import EmptyState from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { ChangeHistoryLink } from "@/components/ChangeHistoryLink";
import { LinkContactModal } from "./components/LinkContactModal";
import { UnlinkLawyerButton } from "../ContentInbox/components/UnlinkLawyerButton";
import { TestimonialDecisionActions } from "./components/TestimonialDecisionActions";
import styles from "./TestimonialDetail.module.css";

const STATUS_CHIPS: Record<string, { label: string; className: string | undefined }> = {
  APPROVED: { label: "Approved", className: styles.statusBadgeApproved },
  REJECTED: { label: "Rejected", className: styles.statusBadgeRejected },
  SUSPENDED: { label: "Suspended", className: styles.statusBadgeSuspended },
};

function StatusChip({ status }: { status: string }) {
  const chip = STATUS_CHIPS[status];
  if (!chip) return null;
  return (
    <Chip size="small" variant="filled" className={chip.className}>
      {chip.label}
    </Chip>
  );
}

function TestimonialDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = `/research/testimonials${location.state?.returnSearch ?? ""}`;
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();

  const [testimonial, setTestimonial] = useState<Testimonial | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [editedContent, setEditedContent] = useState("");
  const [editedLawyerName, setEditedLawyerName] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [relatedTestimonials, setRelatedTestimonials] = useState<Testimonial[]>([]);
  const [linkingContact, setLinkingContact] = useState(false);

  useEffect(() => {
    if (!id) return;

    const fetchTestimonial = async () => {
      setLoading(true);
      try {
        const result = await pipelineService.getTestimonials({ id });
        const item = result.items[0] ?? null;
        setTestimonial(item);
        if (item) {
          setEditedContent(item.content);
          setEditedLawyerName(item.lawyerName ?? "");
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonial();
  }, [id]);

  useEffect(() => {
    if (!testimonial?.externalId) return;

    const fetchRelated = async () => {
      try {
        const result = await pipelineService.getTestimonials({
          externalId: testimonial.externalId!,
          pageSize: 10,
        });
        setRelatedTestimonials(result.items.filter(t => t.id !== testimonial.id));
      } catch {
        // non-critical: related testimonials are optional
      }
    };

    fetchRelated();
  }, [testimonial?.externalId, testimonial?.id]);

  if (loading) {
    return <LoadingState message="Loading testimonial..." />;
  }

  if (error) {
    return (
      <div className={styles.wrapper}>
        <EmptyState
          variant="error"
          title="Failed to load testimonial"
          message={error.message}
          action={{
            label: "Back to List",
            onClick: () => navigate(returnPath),
          }}
        />
      </div>
    );
  }

  if (!testimonial) {
    return (
      <div className={styles.wrapper}>
        <EmptyState
          variant="error"
          title="Testimonial not found"
          message="The requested testimonial could not be found."
          action={{
            label: "Back to List",
            onClick: () => navigate(returnPath),
          }}
        />
      </div>
    );
  }

  const handleBack = () => {
    navigate(returnPath);
  };

  const handleSaveChanges = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await pipelineService.updateTestimonial(id, {
        content: editedContent,
        lawyerName: editedLawyerName || null,
      });
      setToast({ message: "Testimonial saved", type: "success" });
      navigate(returnPath);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save changes";
      setToast({ message: `Request failed: ${msg}`, type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const canUnlinkLawyer =
    hasPermission(TESTIMONIALS_UNLINK_LAWYER_PERMISSION) &&
    (testimonial.contactId != null || !!testimonial.lawyerName);

  const handleUnlinkLawyer = async (testimonialType: UnlinkTestimonialType | undefined) => {
    setSaving(true);
    try {
      await pipelineService.unlinkLawyerFromTestimonial(testimonial.id, testimonialType);
      setTestimonial(prev =>
        prev
          ? {
              ...prev,
              contactId: null,
              lawyerName: null,
              ...(testimonialType ? { testimonialType } : {}),
            }
          : prev,
      );
      setEditedLawyerName("");
      setToast({ message: "Lawyer removed from testimonial", type: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to remove lawyer";
      setToast({ message: `Request failed: ${msg}`, type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // The success message travels with the navigation because this page unmounts
  // on return to the list, which then shows it.
  const leaveToList = (toastMessage?: string) => {
    navigate(returnPath, toastMessage ? { state: { toast: toastMessage } } : undefined);
  };

  const getLanguageFlag = (lang: string): string => {
    const code = lang.toLowerCase().split("-")[0];
    const flagMap: Record<string, string> = {
      en: "\u{1F1EC}\u{1F1E7}",
      pl: "\u{1F1F5}\u{1F1F1}",
      es: "\u{1F1EA}\u{1F1F8}",
      de: "\u{1F1E9}\u{1F1EA}",
      fr: "\u{1F1EB}\u{1F1F7}",
      it: "\u{1F1EE}\u{1F1F9}",
      pt: "\u{1F1F5}\u{1F1F9}",
      zh: "\u{1F1E8}\u{1F1F3}",
      ja: "\u{1F1EF}\u{1F1F5}",
      ko: "\u{1F1F0}\u{1F1F7}",
      ru: "\u{1F1F7}\u{1F1FA}",
      ar: "\u{1F1F8}\u{1F1E6}",
    };
    return flagMap[code] ?? "";
  };

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Testimonial">
          <div style={{ display: "flex", gap: "12px" }}>
            <ChangeHistoryLink source="testimonials" entityId={testimonial.id} />
            <Button variant="outlined" color="default" onClick={handleBack}>
              <Icon icon="arrow-left" size="small" />
              Back
            </Button>
          </div>
        </PageHeader>
      </div>

      <div className={styles.content}>
        <div className={styles.section}>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Status</span>
              <div className={styles.statusContainer}>
                <StatusChip status={testimonial.status} />
              </div>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Language</span>
              <span className={styles.infoValue}>
                {getLanguageFlag(testimonial.language)} {testimonial.language?.toUpperCase() ?? "—"}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Firm</span>
              <span className={styles.infoValue}>
                {testimonial.firmName
                  ? `${testimonial.firmName} (${testimonial.firmRef})`
                  : testimonial.firmRef || "—"}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Publication</span>
              <span className={styles.infoValue}>
                {testimonial.publicationName || testimonial.publicationId || "—"}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Practice Area</span>
              <span className={styles.infoValue}>
                {testimonial.practiceAreaName || testimonial.practiceAreaId || "—"}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Region</span>
              <span className={styles.infoValue}>{testimonial.regionName || "—"}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Contact</span>
              <span className={styles.infoValue}>
                {testimonial.lawyerName ? (
                  <>
                    {testimonial.lawyerName}
                    {testimonial.contactId != null && ` (${testimonial.contactId})`}
                    {testimonial.contactId == null && (
                      <button
                        type="button"
                        className={styles.brokenLinkButton}
                        title="Lawyer not linked to a contact in the system — click to fix"
                        onClick={() => setLinkingContact(true)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="14"
                          height="14"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M9 17H7A5 5 0 0 1 7 7h2" />
                          <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
                          <line x1="8" y1="12" x2="16" y2="12" />
                          <line x1="2" y1="2" x2="22" y2="22" />
                        </svg>
                      </button>
                    )}
                  </>
                ) : (
                  testimonial.contactId || "—"
                )}
                {canUnlinkLawyer && (
                  <UnlinkLawyerButton
                    currentType={testimonial.testimonialType}
                    disabled={saving}
                    onConfirm={handleUnlinkLawyer}
                  />
                )}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>External ID</span>
              <span className={styles.infoValue}>{testimonial.externalId || "—"}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Created</span>
              <span className={styles.infoValue}>
                {testimonial.createdAt ? formatDateTime(testimonial.createdAt) : "—"}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Updated</span>
              <span className={styles.infoValue}>
                {testimonial.updatedAt ? formatDateTime(testimonial.updatedAt) : "—"}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Updated By</span>
              <span className={styles.infoValue}>{testimonial.updatedBy ?? "—"}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Type</span>
              <span className={styles.infoValue}>
                {testimonial.isTranslation ? "Translation" : "Source"}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Research ids</h2>
          <div className={styles.infoGrid}>
            {RESEARCH_ID_FIELDS.map(({ key, label }) => (
              <div key={key} className={styles.infoItem}>
                <span className={styles.infoLabel}>{label}</span>
                <span className={`${styles.infoValue} ${styles.researchIdValue}`}>
                  {testimonial[key] ?? "—"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {relatedTestimonials.length > 0 && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {testimonial.isTranslation ? "Source Testimonial" : "Translation"}
            </h2>
            <div className={styles.relatedList}>
              {relatedTestimonials.map(related => (
                <div key={related.id} className={styles.relatedItem}>
                  <div className={styles.relatedTop}>
                    <span className={styles.relatedLang}>
                      {getLanguageFlag(related.language)} {related.language?.toUpperCase()}
                    </span>
                    <StatusChip status={related.status} />
                    <span className={styles.relatedContent}>
                      {related.content.slice(0, 80)}
                      {related.content.length > 80 ? "..." : ""}
                    </span>
                  </div>
                  <span className={styles.relatedLinkLabel}>
                    Link:{" "}
                    <a
                      className={styles.relatedLink}
                      href={`/research/testimonials/${related.id}`}
                      onClick={e => {
                        e.preventDefault();
                        navigate(`/research/testimonials/${related.id}`);
                      }}
                    >
                      /research/testimonials/{related.id}
                    </a>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Content</h2>
          <Textarea
            value={editedContent}
            onChange={e => setEditedContent(e.target.value)}
            className={styles.editableTextarea}
            rows={8}
          />
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Lawyer Name</h2>
          <input
            type="text"
            className={styles.lawyerNameInput}
            value={editedLawyerName}
            onChange={e => setEditedLawyerName(e.target.value)}
            placeholder="Lawyer name (optional)"
          />
        </div>
      </div>

      <div className={styles.actionsSection}>
        <Button variant="contained" color="primary" onClick={handleSaveChanges} disabled={saving}>
          Save Changes
        </Button>
        <TestimonialDecisionActions
          testimonial={testimonial}
          saving={saving}
          onSavingChange={setSaving}
          onToast={setToast}
          onLeave={leaveToList}
        />
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={toast.type === "error" ? 5000 : 3000}
        />
      )}

      {linkingContact && testimonial && (
        <LinkContactModal
          item={testimonial}
          onClose={() => setLinkingContact(false)}
          onLinked={({ contactId, lawyerName }) => {
            setLinkingContact(false);
            setTestimonial(prev => (prev ? { ...prev, contactId, lawyerName } : prev));
          }}
        />
      )}
    </div>
  );
}

export default TestimonialDetail;
